import type {Component, PropertyProps} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';
import type PresetComponentVO from '../vos/presetcompvo';

export interface PresetComponentSelectorProps extends PropertyProps {
  component?: Component;
  defaultPresetId?: number;
  list: PresetComponentVO[];
  callback?: (component: Component, preset: PresetComponentVO) => void;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  /**
   * Allow to select a component from a range of predefined styles of components
   */
  styleManager.addType(window.PMW.emails.styles.ID_PRESET_SELECTOR, {
    create(data) {
      const {change} = data;
      const props = data.props as PresetComponentSelectorProps;
      props.component = props.component ?? editor.getSelected();
      const currComponent = props.component;

      if (!currComponent) {
        throw new Error('No component selected');
      }

      const componentSelector = document.createElement('div');
      const selectedPresetID: unknown = currComponent.get(props.property ?? '');
      const presetId = typeof selectedPresetID === 'number' ? selectedPresetID : (props.defaultPresetId ?? 0);

      componentSelector.className = props.type ?? '';
      componentSelector.innerHTML = getCompPresetsHTML(props, presetId);

      attachComponentPresetHandler(props, change, componentSelector);
      return componentSelector;
    },

    emit(data, eventData: {presetEl: Element; presetID: number}) {
      const props = data.props as PresetComponentSelectorProps;
      const {presetEl, presetID} = eventData;

      // Replace jQuery with vanilla JavaScript
      presetEl.classList.add('-selected');
      const siblings = presetEl.parentElement?.querySelectorAll('.preset-wrapper');
      siblings?.forEach((sibling) => {
        if (sibling !== presetEl) {
          sibling.classList.remove('-selected');
        }
      });

      if (props.component && props.property) {
        props.component.set(props.property, presetID);
      }

      if (typeof props.callback === 'function' && props.component) {
        const presetIndex = props.list.findIndex((presetCompVO) => {
          return presetCompVO.id === presetID;
        });
        if (presetIndex >= 0) {
          props.callback(props.component, props.list[presetIndex]);
        }
      }
    },
  });

  /**
   * Attaches click handlers to preset components
   */
  function attachComponentPresetHandler(
    props: PresetComponentSelectorProps,
    change: (data: {componentSelector: HTMLElement; presetEl: Element; presetID: number}) => void,
    componentSelector: HTMLElement
  ): void {
    componentSelector.querySelectorAll('.js-comp-preset').forEach((presetEl) => {
      const presetID = parseInt(presetEl.getAttribute('data-preset-id') ?? '0', 10);

      presetEl.addEventListener('click', function () {
        change({componentSelector, presetEl, presetID});
      });
    });
  }

  /**
   * Generates HTML markup for component presets
   */
  function getCompPresetsHTML(props: PresetComponentSelectorProps, selectedPresetID: number): string {
    return (
      `<div class="comp-presets">` +
      props.list
        .map((preset) => {
          return preset.render(selectedPresetID);
        })
        .join('') +
      `</div>`
    );
  }
};
